import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import Overline from "@/components/Overline";
import BrandMarquee from "@/components/BrandMarquee";
import { BRANDS } from "@/data/site";
import { useQuoteModal } from "@/context/QuoteModalContext";

export default function Brands() {
  const { openModal } = useQuoteModal();
  return (
    <div data-testid="brands-page" className="border-b border-slate-200">
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="tech-grid tech-grid-fade absolute inset-0 opacity-50" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-28">
          <Overline>Brands & Business Partners</Overline>
          <h1 className="font-heading mt-6 max-w-3xl text-4xl font-semibold tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
            Trusted brands. Authorised channels. Genuine products.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-700 sm:text-lg">
            We supply and trade products from the industry&apos;s most established
            technology manufacturers — sourced through authorised distribution
            networks and global trading partners.
          </p>
        </div>
      </section>

      <BrandMarquee />

      <section>
        <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8 lg:py-24">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
            <div className="max-w-2xl">
              <Overline>Partner Directory</Overline>
              <h2 className="font-heading mt-5 text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl">
                A selection of brands we work with.
              </h2>
            </div>
            <p className="max-w-md text-sm text-slate-500">
              Looking for a specific manufacturer or product? Reach out and
              we&apos;ll confirm availability and pricing.
            </p>
          </div>

          <ul
            data-testid="brand-grid"
            className="mt-12 grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 sm:grid-cols-3 lg:grid-cols-4"
          >
            {BRANDS.map((b, idx) => (
              <li
                key={b.name}
                data-testid={`brand-${idx}`}
                className="group flex h-36 flex-col items-center justify-center gap-3 bg-white px-4 transition-colors hover:bg-slate-50"
              >
                <img
                  src={b.logo || `https://cdn.simpleicons.org/${b.slug}`}
                  alt={`${b.name} logo`}
                  loading="lazy"
                  className="h-10 max-w-[140px] object-contain opacity-90 transition-opacity duration-300 group-hover:opacity-100"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
                <span className="font-mono text-xs uppercase tracking-[0.28em] text-slate-500 transition-colors group-hover:text-cyan-700">
                  {b.name}
                </span>
              </li>
            ))}
          </ul>

          <div className="mt-12 rounded-xl border border-slate-200 bg-slate-50 p-8 sm:p-10">
            <div className="grid gap-6 lg:grid-cols-12 lg:items-center">
              <div className="lg:col-span-8">
                <h3 className="font-heading text-2xl font-medium text-slate-900">
                  Don&apos;t see your preferred brand?
                </h3>
                <p className="mt-3 max-w-xl text-sm text-slate-500">
                  Our sourcing network extends well beyond this list. Send us
                  a request and we&apos;ll come back with availability.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <Button
                  data-testid="brands-cta-quote-btn"
                  onClick={() => openModal()}
                  className="rounded-md bg-cyan-600 px-6 py-5 font-medium text-white hover:bg-cyan-700"
                >
                  Ask about a brand
                  <ArrowUpRight className="ml-1.5 h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
